import * as THREE from 'three';
import {
  WORLD_SIZE_X,
  WORLD_SIZE_Z,
  WORLD_HEIGHT,
  WORLD_MIN_Y,
  CHUNK_SIZE,
  BLOCK_AIR,
  BLOCK_WHITE,
  BLOCK_RENDER_DISTANCE,
} from './constants';
import { ChunkRenderer } from './ChunkRenderer';
import { CustomBlockRenderer } from './CustomBlockRenderer';
import { CraftBlockRenderer } from './CraftBlockRenderer';
import { GlowLightManager } from './GlowLightManager';
import { LibraryBlockRenderer } from './LibraryBlockRenderer';
import type { AnimationFlags } from './animationControl';
import {
  CRAFT_BLOCK_TYPE,
  LIBRARY_BLOCK_TYPE_BASE,
  CustomBlockDefinition,
  LibraryBlockDefinition,
  DEFAULT_BLOCK_COLLISION,
  FULL_CELL_HITBOX,
  blockTransformMatrix,
  customBlockId,
  libraryBlockId,
  resolvePlaceViewId as pickPlacementViewId,
  tightHitboxFromVoxels,
  transformCellHitbox,
  worldHitboxFromCell,
  zoneIndexAt,
  type CellHitbox,
  ZoneState,
} from '@shared/protocol';

/**
 * Implicit 20-block-deep terrain plus sparse cell overrides.
 *
 * Untouched chunks are slab instances. A chunk containing any override
 * (including an explicit air hole) becomes a greedy mesh.
 */
export class World {
  readonly group = new THREE.Group();

  /** Cells differing from implicit terrain. Type 0 means explicit air. */
  private readonly overrides = new Map<number, number>();
  private readonly transforms = new Map<number, number>();
  private readonly viewIds = new Map<number, number>();
  private readonly animOrigins = new Map<number, number>();
  private readonly chunkOverrideCounts = new Map<number, number>();
  private readonly glowLights = new GlowLightManager();
  private readonly customRenderer = new CustomBlockRenderer(this.glowLights);
  private readonly libraryRenderer = new LibraryBlockRenderer(this.glowLights);
  private readonly craftRenderer = new CraftBlockRenderer();
  private readonly craftBlocks = new Map<number, { x: number; y: number; z: number }>();
  private readonly customDefinitions = new Map<number, CustomBlockDefinition>();
  private readonly libraryDefinitions = new Map<number, LibraryBlockDefinition>();
  /** Cached local (0..1) hitboxes for custom shrink; key = `${id}:${transform}`. */
  private readonly localHitboxCache = new Map<string, CellHitbox>();
  private readonly renderer = new ChunkRenderer((x, y, z) => {
    const type = this.getBlock(x, y, z);
    return type >= LIBRARY_BLOCK_TYPE_BASE || type === CRAFT_BLOCK_TYPE
      ? BLOCK_AIR
      : type;
  });

  zones: ZoneState[] = [];
  selfId = '';

  constructor() {
    this.group.add(this.renderer.group);
    this.group.add(this.customRenderer.group);
    this.group.add(this.libraryRenderer.group);
    this.group.add(this.craftRenderer.group);
    this.group.add(this.glowLights.group);
    this.group.add(this.createZoneBorders());
  }

  addCustomDefinitions(definitions: CustomBlockDefinition[]): void {
    for (const definition of definitions) {
      this.customDefinitions.set(definition.id, definition);
      for (const key of [...this.localHitboxCache.keys()]) {
        if (key.startsWith(`${definition.id}:`)) this.localHitboxCache.delete(key);
      }
    }
    this.customRenderer.addDefinitions(definitions);
  }

  addLibraryDefinitions(definitions: LibraryBlockDefinition[]): void {
    for (const definition of definitions) {
      this.libraryDefinitions.set(definition.id, definition);
    }
    this.libraryRenderer.addDefinitions(definitions);
  }

  /**
   * Player physics collider in world space, or null when the cell is passable.
   * Build/raycast still use full-cell `isSolid`.
   */
  getPlayerCollider(x: number, y: number, z: number): CellHitbox | null {
    const type = this.getBlock(x, y, z);
    if (type === BLOCK_AIR) return null;

    const customId = customBlockId(type);
    if (customId !== null) {
      const definition = this.customDefinitions.get(customId);
      const collision = definition?.collision ?? DEFAULT_BLOCK_COLLISION;
      if (!collision.collideWithPlayer) return null;
      if (collision.shrinkHitbox && definition && definition.voxels.length > 0) {
        const transform = this.getBlockTransform(x, y, z);
        const cacheKey = `${customId}:${transform}`;
        let local = this.localHitboxCache.get(cacheKey);
        if (!local) {
          const tight = tightHitboxFromVoxels(definition.voxels, definition.gridSize)
            ?? FULL_CELL_HITBOX;
          local = transform === 0
            ? tight
            : transformCellHitbox(tight, blockTransformMatrix(transform));
          this.localHitboxCache.set(cacheKey, local);
        }
        return worldHitboxFromCell(x, y, z, local);
      }
      return worldHitboxFromCell(x, y, z, FULL_CELL_HITBOX);
    }

    const libId = libraryBlockId(type);
    if (libId !== null) {
      const definition = this.libraryDefinitions.get(libId);
      const collision = definition?.collision ?? DEFAULT_BLOCK_COLLISION;
      if (!collision.collideWithPlayer) return null;
      return worldHitboxFromCell(x, y, z, FULL_CELL_HITBOX);
    }

    return worldHitboxFromCell(x, y, z, FULL_CELL_HITBOX);
  }

  getCraftBlocks(): ReadonlyArray<{ x: number; y: number; z: number }> {
    return [...this.craftBlocks.values()];
  }

  /** Thin dark lines marking the four ownership quadrants. */
  private createZoneBorders(): THREE.Group {
    const group = new THREE.Group();
    const material = new THREE.MeshBasicMaterial({ color: 0x8899aa });
    const alongX = new THREE.Mesh(
      new THREE.BoxGeometry(WORLD_SIZE_X, 0.02, 0.12),
      material,
    );
    alongX.position.set(WORLD_SIZE_X / 2, 1.005, WORLD_SIZE_Z / 2);
    const alongZ = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.02, WORLD_SIZE_Z),
      material,
    );
    alongZ.position.set(WORLD_SIZE_X / 2, 1.005, WORLD_SIZE_Z / 2);
    group.add(alongX, alongZ);
    return group;
  }

  canEditAt(x: number, z: number): boolean {
    if (x < 0 || x >= WORLD_SIZE_X || z < 0 || z >= WORLD_SIZE_Z) return false;
    return this.zones[zoneIndexAt(x, z)]?.ownerId === this.selfId && this.selfId !== '';
  }

  /** Delete every override in a zone, restoring pristine implicit terrain. */
  clearZone(zoneIndex: number): void {
    const changedChunks = new Set<number>();
    for (const key of [...this.overrides.keys()]) {
      const { x, y, z } = unpackCellKey(key);
      if (zoneIndexAt(x, z) !== zoneIndex) continue;
      this.customRenderer.setCell(x, y, z, BLOCK_AIR, 0, 0, 0);
      this.libraryRenderer.setCell(x, y, z, BLOCK_AIR, 0, 0, 0);
      this.craftRenderer.setCell(x, y, z, BLOCK_AIR);
      this.craftBlocks.delete(key);
      this.overrides.delete(key);
      this.transforms.delete(key);
      this.viewIds.delete(key);
      this.animOrigins.delete(key);
      changedChunks.add(chunkKeyAt(x, z));
    }
    this.rebuildChunkOverrideCounts();
    for (const key of changedChunks) {
      const [chunkX, chunkZ] = unpackChunkKey(key);
      this.renderer.invalidateChunk(chunkX, chunkZ);
    }
  }

  getBlock(x: number, y: number, z: number): number {
    if (x < 0 || x >= WORLD_SIZE_X || z < 0 || z >= WORLD_SIZE_Z) return BLOCK_AIR;
    if (y < WORLD_MIN_Y || y >= WORLD_HEIGHT) return BLOCK_AIR;
    return this.overrides.get(cellKey(x, y, z)) ?? defaultBlockAt(y);
  }

  getBlockTransform(x: number, y: number, z: number): number {
    return this.transforms.get(cellKey(x, y, z)) ?? 0;
  }

  getBlockViewId(x: number, y: number, z: number): number {
    return this.viewIds.get(cellKey(x, y, z)) ?? 0;
  }

  getBlockAnimOrigin(x: number, y: number, z: number): number {
    return this.animOrigins.get(cellKey(x, y, z)) ?? 0;
  }

  /** Resolve view id for a new placement (random snapshot or follow-active 0). */
  pickPlacementViewId(typeId: number): number {
    const customId = customBlockId(typeId);
    if (customId !== null) {
      const definition = this.customDefinitions.get(customId);
      return definition
        ? pickPlacementViewId(definition.activeViewId, definition.views)
        : 0;
    }
    const libId = libraryBlockId(typeId);
    if (libId !== null) {
      const definition = this.libraryDefinitions.get(libId);
      return definition
        ? pickPlacementViewId(definition.activeViewId, definition.views)
        : 0;
    }
    return 0;
  }

  isSolid(x: number, y: number, z: number): boolean {
    return this.getBlock(x, y, z) !== BLOCK_AIR;
  }

  /** Place into an air cell at any buildable Y, including excavated terrain. */
  setBlock(x: number, y: number, z: number, typeId: number): boolean {
    if (x < 0 || x >= WORLD_SIZE_X || z < 0 || z >= WORLD_SIZE_Z) return false;
    if (y < WORLD_MIN_Y || y >= WORLD_HEIGHT || typeId === BLOCK_AIR) return false;
    if (this.getBlock(x, y, z) !== BLOCK_AIR) return false;
    this.setCellState(x, y, z, typeId, 0);
    return true;
  }

  /** Remove a solid cell; implicit base cells become explicit air holes. */
  removeBlock(x: number, y: number, z: number): boolean {
    if (this.getBlock(x, y, z) === BLOCK_AIR) return false;
    this.setCellState(x, y, z, BLOCK_AIR, 0);
    return true;
  }

  /** Apply a persisted/network override, including type-0 terrain holes. */
  applyOverride(
    x: number,
    y: number,
    z: number,
    typeId: number,
    transform = 0,
    viewId = 0,
    animOrigin = 0,
  ): void {
    this.setBlockState(x, y, z, typeId, transform, viewId, animOrigin);
  }

  /** Set an absolute cell state for server batches and undo/redo. */
  setBlockState(
    x: number,
    y: number,
    z: number,
    typeId: number,
    transform = 0,
    viewId = 0,
    animOrigin = 0,
  ): boolean {
    if (x < 0 || x >= WORLD_SIZE_X || z < 0 || z >= WORLD_SIZE_Z) return false;
    if (y < WORLD_MIN_Y || y >= WORLD_HEIGHT) return false;
    const normalizedTransform = typeId >= LIBRARY_BLOCK_TYPE_BASE ? transform : 0;
    const normalizedViewId = typeId >= LIBRARY_BLOCK_TYPE_BASE ? viewId : 0;
    const normalizedAnimOrigin = typeId >= LIBRARY_BLOCK_TYPE_BASE ? animOrigin : 0;
    if (
      this.getBlock(x, y, z) === typeId &&
      this.getBlockTransform(x, y, z) === normalizedTransform &&
      this.getBlockViewId(x, y, z) === normalizedViewId &&
      this.getBlockAnimOrigin(x, y, z) === normalizedAnimOrigin
    ) return false;
    this.setCellState(
      x, y, z, typeId, normalizedTransform, normalizedViewId, normalizedAnimOrigin,
    );
    return true;
  }

  setAnimationTick(worldTick: number): void {
    this.customRenderer.setAnimationTick(worldTick);
    this.libraryRenderer.setAnimationTick(worldTick);
  }

  setAnimationFlags(flags: AnimationFlags): void {
    this.customRenderer.setAnimationFlags(flags);
    this.libraryRenderer.setAnimationFlags(flags);
  }

  setGlowEnabled(enabled: boolean): void {
    this.glowLights.setEnabled(enabled);
    this.customRenderer.setGlowVisualsEnabled(enabled);
    this.libraryRenderer.setGlowVisualsEnabled(enabled);
  }

  setRenderDistance(blocks: number): void {
    this.renderer.setRenderDistanceBlocks(blocks);
  }

  updateRenderDistance(position: THREE.Vector3): void {
    this.renderer.update(position.x, position.z);
    this.customRenderer.update();
    this.libraryRenderer.update();
    this.craftRenderer.update();
    this.glowLights.update(position.x, position.y, position.z);
  }

  isInsideBorder(x: number, z: number, margin = 0.3): boolean {
    return (
      x >= margin &&
      x <= WORLD_SIZE_X - margin &&
      z >= margin &&
      z <= WORLD_SIZE_Z - margin
    );
  }

  private setCellState(
    x: number,
    y: number,
    z: number,
    typeId: number,
    transform: number,
    viewId = 0,
    animOrigin = 0,
  ): void {
    const key = cellKey(x, y, z);
    const chunk = chunkKeyAt(x, z);
    const hadOverride = this.overrides.has(key);

    if (typeId === defaultBlockAt(y)) {
      if (!hadOverride) return;
      this.overrides.delete(key);
      this.transforms.delete(key);
      this.viewIds.delete(key);
      this.animOrigins.delete(key);
      this.adjustChunkOverrideCount(chunk, -1);
    } else {
      this.overrides.set(key, typeId);
      if (transform === 0) this.transforms.delete(key);
      else this.transforms.set(key, transform);
      if (viewId === 0) this.viewIds.delete(key);
      else this.viewIds.set(key, viewId);
      if (animOrigin === 0) this.animOrigins.delete(key);
      else this.animOrigins.set(key, animOrigin);
      if (!hadOverride) this.adjustChunkOverrideCount(chunk, 1);
    }
    this.customRenderer.setCell(x, y, z, typeId, transform, viewId, animOrigin);
    this.libraryRenderer.setCell(x, y, z, typeId, transform, viewId, animOrigin);
    this.craftRenderer.setCell(x, y, z, typeId);
    if (typeId === CRAFT_BLOCK_TYPE) {
      this.craftBlocks.set(key, { x, y, z });
    } else {
      this.craftBlocks.delete(key);
    }
    this.renderer.invalidate(x, z);
  }

  private adjustChunkOverrideCount(key: number, delta: number): void {
    const next = (this.chunkOverrideCounts.get(key) ?? 0) + delta;
    const [chunkX, chunkZ] = unpackChunkKey(key);
    if (next <= 0) {
      this.chunkOverrideCounts.delete(key);
      this.renderer.setChunkEdited(chunkX, chunkZ, false);
    } else {
      this.chunkOverrideCounts.set(key, next);
      this.renderer.setChunkEdited(chunkX, chunkZ, true);
    }
  }

  private rebuildChunkOverrideCounts(): void {
    const oldKeys = new Set(this.chunkOverrideCounts.keys());
    this.chunkOverrideCounts.clear();
    for (const key of this.overrides.keys()) {
      const { x, z } = unpackCellKey(key);
      const chunk = chunkKeyAt(x, z);
      this.chunkOverrideCounts.set(chunk, (this.chunkOverrideCounts.get(chunk) ?? 0) + 1);
    }
    for (const key of oldKeys) {
      if (this.chunkOverrideCounts.has(key)) continue;
      const [chunkX, chunkZ] = unpackChunkKey(key);
      this.renderer.setChunkEdited(chunkX, chunkZ, false);
    }
  }
}

function defaultBlockAt(y: number): number {
  return y >= WORLD_MIN_Y && y <= 0 ? BLOCK_WHITE : BLOCK_AIR;
}

/** Y is offset by WORLD_MIN_Y so every packed key stays non-negative. */
function cellKey(x: number, y: number, z: number): number {
  return x + z * WORLD_SIZE_X + (y - WORLD_MIN_Y) * WORLD_SIZE_X * WORLD_SIZE_Z;
}

function unpackCellKey(key: number): { x: number; y: number; z: number } {
  const layerSize = WORLD_SIZE_X * WORLD_SIZE_Z;
  const yOffset = Math.floor(key / layerSize);
  const layerCell = key % layerSize;
  return {
    x: layerCell % WORLD_SIZE_X,
    y: yOffset + WORLD_MIN_Y,
    z: Math.floor(layerCell / WORLD_SIZE_X),
  };
}

const CHUNKS_X = WORLD_SIZE_X / CHUNK_SIZE;

function chunkKeyAt(x: number, z: number): number {
  return Math.floor(x / CHUNK_SIZE) + Math.floor(z / CHUNK_SIZE) * CHUNKS_X;
}

function unpackChunkKey(key: number): [number, number] {
  return [key % CHUNKS_X, Math.floor(key / CHUNKS_X)];
}

export const SUN_OFFSET = new THREE.Vector3(60, 120, 40);

export function setupSky(scene: THREE.Scene): THREE.DirectionalLight {
  const skyColor = new THREE.Color(0x87b9e8);
  scene.background = skyColor;
  scene.fog = new THREE.Fog(
    skyColor,
    BLOCK_RENDER_DISTANCE * 0.72,
    BLOCK_RENDER_DISTANCE,
  );

  const ambient = new THREE.HemisphereLight(0xcfe8ff, 0xe8e8e8, 0.9);
  scene.add(ambient);

  const sun = new THREE.DirectionalLight(0xfff4e0, 1.6);
  sun.position.copy(SUN_OFFSET);
  sun.castShadow = true;
  // Higher map + tighter frustum = sharper block shadows; Dev panel can retune size.
  sun.shadow.mapSize.set(4096, 4096);
  sun.shadow.bias = -0.0002;
  sun.shadow.normalBias = 0.035;
  sun.shadow.camera.near = 10;
  sun.shadow.camera.far = 320;
  const shadowExtent = 28;
  sun.shadow.camera.left = -shadowExtent;
  sun.shadow.camera.right = shadowExtent;
  sun.shadow.camera.top = shadowExtent;
  sun.shadow.camera.bottom = -shadowExtent;
  sun.shadow.camera.updateProjectionMatrix();
  scene.add(sun);
  scene.add(sun.target);
  return sun;
}
